import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ShieldAlert, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  children: ReactNode;
  title?: string;
  description?: string;
  onReset?: () => void;
  className?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  isResetting: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      isResetting: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("[Component Error Boundary caught]:", error, errorInfo);
  }

  handleReset = () => {
    // React.lazy caches a rejected import. Resetting this boundary alone cannot
    // retry it; start a fresh module graph when the user requests recovery.
    if (this.state.error && /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk .+ failed/i.test(this.state.error.message)) {
      window.location.reload();
      return;
    }
    this.setState({ isResetting: true });
    
    // Smooth reset delay to simulate intelligence core stabilization
    setTimeout(() => {
      if (this.props.onReset) {
        this.props.onReset();
      }
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        showDetails: false,
        isResetting: false
      });
    }, 800);
  };

  render() {
    if (this.state.hasError) {
      const title = this.props.title || "Protocol Sync Interrupted";
      const description = this.props.description || "The intelligence core encountered an unexpected state. Restoring local lattice synchronization protocols.";

      return (
        <div className={`flex flex-col items-center justify-center p-8 rounded-[2rem] border border-red-500/10 bg-[#0D1321]/90 backdrop-blur-sm text-white font-['Outfit',sans-serif] text-center min-h-[300px] w-full max-w-2xl mx-auto shadow-2xl transition-all ${this.props.className || ''}`}>
          <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 border border-red-500/20 shadow-lg shadow-red-500/5 animate-pulse">
            <ShieldAlert className="text-red-400" size={24} />
          </div>

          <h3 className="text-lg font-black uppercase tracking-tighter text-white mb-2">
            {title}
          </h3>
          <p className="text-white/40 text-xs font-semibold max-w-sm mb-6 leading-relaxed">
            {description}
          </p>

          <div className="flex flex-col items-center gap-4 w-full">
            <button
              onClick={this.handleReset}
              disabled={this.state.isResetting}
              className="px-6 py-2.5 bg-white hover:bg-white/95 text-[#0A0F1E] rounded-xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2 active:scale-95 transition-all shadow-md shadow-white/5 cursor-pointer disabled:opacity-50"
            >
              <RefreshCw size={12} className={this.state.isResetting ? "animate-spin" : ""} />
              {this.state.isResetting ? "Stabilizing Core..." : "Retry Protocol"}
            </button>

            {this.state.error && (
              <div className="w-full mt-4 border border-white/5 rounded-2xl bg-[#080C16] overflow-hidden text-left">
                <button
                  onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
                  className="w-full px-4 py-3 flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-white/30 hover:text-white/50 transition-colors"
                >
                  <span>Diagnostic Trace</span>
                  {this.state.showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>

                {this.state.showDetails && (
                  <div className="px-4 pb-4 font-mono text-[10px] text-rose-400/80 overflow-x-auto max-h-40 select-text selection:bg-rose-500/20 leading-relaxed border-t border-white/5 pt-3">
                    <div className="font-bold mb-1">{this.state.error.toString()}</div>
                    {this.state.errorInfo?.componentStack && (
                      <pre className="text-white/20 whitespace-pre text-[9px] mt-2">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
